import useRequest from "@/lib/request/useRequest";
import { FieldConfig } from "@/types/fieldConfig";
import {
    Button,
    ModalContent,
    ModalBody,
    ModalHeader,
    Modal,
    ModalFooter,
    DatePicker,
} from "@heroui/react";
import { useState, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import {
    DateValue,
    parseDate,
    getLocalTimeZone,
} from "@internationalized/date";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard } from "@fortawesome/free-solid-svg-icons";

function BulkLogRxApplied({
    selectedFields,
    onFinish,
}: {
    selectedFields: FieldConfig[];
    onFinish: () => void;
}) {
    const { useMutateRequest } = useRequest();

    const [applicationDate, setApplicationDate] = useState<DateValue | null>(
        parseDate(dayjs().format("YYYY-MM-DD")),
    );

    const fieldsHaveApplications = useMemo(() => {
        return selectedFields.some(
            (field) => field.latest_prescription_application !== null,
        );
    }, [selectedFields]);

    const bulkLogRxAppliedMutation = useMutateRequest({
        url: "application_operations/prescriptions/",
        method: "put",
        successMessage: "Logged Rx applications successfully.",
        errorMessage: "Failed to log Rx applications.",
    });

    const handleLogApplications = useCallback(() => {
        const prescriptionIds = selectedFields
            .map((field) => field.latest_plot_prescription_id)
            .filter((id) => id !== null);
        bulkLogRxAppliedMutation.mutate(
            {
                data: prescriptionIds.map((id) => ({
                    prescription: id,
                    start_date: dayjs(
                        applicationDate?.toDate(getLocalTimeZone()),
                    ).format("YYYY-MM-DD"),
                })),
            },
            {
                onSuccess: () => {
                    onFinish();
                },
            },
        );
    }, [bulkLogRxAppliedMutation, selectedFields, applicationDate, onFinish]);

    return (
        <Modal isOpen size="md" onClose={onFinish}>
            <ModalContent>
                <ModalHeader>Log Plot Applications</ModalHeader>
                <ModalBody>
                    <DatePicker
                        label="Application Date"
                        value={applicationDate}
                        onChange={(date) => setApplicationDate(date)}
                    />
                    {fieldsHaveApplications && (
                        <div className="mt-4 text-sm text-warning text-center">
                            Existing applications for the selected prescriptions
                            will be updated to completed on the new date.
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        onPress={handleLogApplications}
                        isLoading={bulkLogRxAppliedMutation.isPending}
                        fullWidth
                    >
                        <FontAwesomeIcon icon={faClipboard} className="mr-2" />
                        Log Applications
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default BulkLogRxApplied;
